import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";
import axios from "axios";
import { API } from "../../../config";
import { setSuccessShareDeleteBins } from "../../slices/shareSite/shareSite";
import { SharingStatusData } from "../../slices/shareSite/shareSiteTypes";
import { StoragesValuesView } from "../../slices/grainTracker/grainTrackerTypes";

export const fetchListOfPartnerOrganisations = createAsyncThunk(
  "ShareSiteReducer/fetchListOfPartnerOrganisations",
  async (_: null, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;
    const token = user?.token ?? "";

    try {
      const response = await axios.get(`${API}/storages/sharing-receiver`, {
        headers: {
          "Accept-Language": `${locale}`,
          Authorization: `Bearer ${token}`,
        },
      });
      const { data } = response;

      return store.fulfillWithValue(data);
    } catch (error: any) {
      return store.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const fetchStoragesOfEachLocationLocationView = createAsyncThunk(
  "ShareSiteReducer/fetchStoragesOfEachLocationLocationView",
  async (arg: { orgGeoPointId: string }, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;
    const token = user?.token ?? "";

    try {
      const response = await axios.get(
        `${API}/storages/storage-instance?orgGeoPointId=${arg.orgGeoPointId}&$eager=[storageTemplate, cerealType]`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const { data } = response;

      const filterUndefinedData = data.data.filter(
        (s: StoragesValuesView) => s.totalCapacity.value !== 0,
      );

      const sortedData = {
        total: data.total,
        limit: data.limit,
        skip: data.skip,
        data: filterUndefinedData.toSorted((a: StoragesValuesView): number => {
          if (a.usedCapacity.value) return -1;
          return 0;
        }),
      };

      return store.fulfillWithValue(sortedData);
    } catch (error: any) {
      return store.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const fetchingSharingStatus = createAsyncThunk(
  "ShareSiteReducer/fetchingSharingStatus",
  async (arg: { orgGeoPointId: string; selectedPatner: number }, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;
    const token = user?.token ?? "";

    try {
      const response = await axios.get(
        `${API}/storages/storage-sharing?$joinRelation=storageInstance&storageInstance.orgGeoPointId=${arg.orgGeoPointId}`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const { data } = response;

      const filterDataByPartner = data.data.filter(
        (val: SharingStatusData) => val.sharedToOrgId === arg.selectedPatner,
      );
      const newDataObject = {
        total: filterDataByPartner.length,
        limit: data.limit,
        skip: data.skip,
        data: filterDataByPartner,
      };
      return store.fulfillWithValue(newDataObject);
    } catch (error: any) {
      return store.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const storageSharing = createAsyncThunk(
  "ShareSiteReducer/storageSharing",
  async (
    arg: {
      storageInstanceId: string;
      sharedToOrgId: string;
      sharedByOrgId: number;
    },
    store,
  ) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;
    const token = user?.token ?? "";

    try {
      const response = await axios.post(
        `${API}/storages/storage-sharing`,
        {
          storageInstanceId: arg.storageInstanceId,
          sharedToOrgId: arg.sharedToOrgId,
          sharedByOrgId: arg.sharedByOrgId,
        },
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const { data } = response;
      return store.fulfillWithValue(data);
    } catch (error: any) {
      return store.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const deleteStorageSharing = createAsyncThunk(
  "ShareSiteReducer/deleteStorageSharing",
  async (arg: { sharingStorageId: string }, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;
    const token = user?.token ?? "";

    try {
      const response = await axios.delete(
        `${API}/storages/storage-sharing/${arg.sharingStorageId}`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const { data } = response;

      return store.fulfillWithValue(data);
    } catch (error: any) {
      return store.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const multiStorageSharing = createAsyncThunk(
  "ShareSiteReducer/storageSharing",
  async (
    arg: {
      selectedStoragesToNotShare: string[];
      selectedStoragesToShare: string[];
      theCorrectIdsToDeleteSharedStorage: string[];
    },
    store,
  ) => {
    const { selectedPatner, toDelete } = (store.getState() as RootState)
      .ShareSiteReducer;

    const { orgId } = (store.getState() as RootState).Authentication;

    try {
      if (arg.selectedStoragesToShare.length >= 1) {
        const shareStoragePromises = arg.selectedStoragesToShare?.map(
          (id: string) => {
            return store.dispatch(
              storageSharing({
                storageInstanceId: id,
                sharedToOrgId: selectedPatner,
                sharedByOrgId: orgId as number,
              }),
            );
          },
        );
        await Promise.all(shareStoragePromises);
      }

      if (toDelete.length >= 1) {
        const deleteStoragePromises =
          arg.theCorrectIdsToDeleteSharedStorage.map((id: string) => {
            return store.dispatch(
              deleteStorageSharing({
                sharingStorageId: id,
              }),
            );
          });

        await Promise.all(deleteStoragePromises);
      }
      store.dispatch(setSuccessShareDeleteBins(true));
      store.fulfillWithValue("success");
    } catch (error: any) {
      return store.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const listSpecies = createAsyncThunk(
  "ShareSiteReducer/listSpecies",
  async (_: null, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;
    const token = user?.token ?? "";

    try {
      const response = await axios.get(
        `${API}/core/cereal-type?$select[0]=id&$select[1]=slug&$select[2]=textId&$select[3]=conversionFromWheatTons`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const { data } = response;
      return store.fulfillWithValue(data);
    } catch (error: any) {
      return store.rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const typeOfBin = createAsyncThunk(
  "ShareSiteReducer/typeOfBin",
  async (_: null, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;
    const token = user?.token ?? "";

    try {
      const response = await axios.get(`${API}/storages/storage-template`, {
        headers: {
          "Accept-Language": `${locale}`,
          Authorization: `Bearer ${token}`,
        },
      });
      const { data } = response;

      return store.fulfillWithValue(data);
    } catch (error: any) {
      return store.rejectWithValue(error?.response?.data?.message);
    }
  },
);
